import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import type { StepExecutionItem } from "features/integrationsBeta/integrationsBeta";
import type { TimezoneDisplayMode } from "features/integrationsBeta/timezoneDisplay";
import { formatIsoDuration } from "utils/dateUtils";
import PayloadView from "./PayloadView";
import RunStatusChip from "./RunStatusChip";
import RunTimestamp from "./RunTimestamp";
import StageChip from "./StageChip";

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body1"
        component="div"
        sx={{ wordBreak: "break-word" }}
      >
        {value}
      </Typography>
    </Box>
  );
}

type Props = {
  step: StepExecutionItem;
  statusLabel: string;
  stageLabel: string;
  timezoneMode: TimezoneDisplayMode;
  displayZone?: string;
};

export default function StepExecutionDetails({
  step,
  statusLabel,
  stageLabel,
  timezoneMode,
  displayZone,
}: Props) {
  const details = (step.details ?? {}) as Record<string, unknown>;
  const { error, error_detail, ...payload } = details;
  const errorMessage = typeof error === "string" ? error : null;
  const errorDetail = typeof error_detail === "string" ? error_detail : null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5} mb={2} flexWrap="wrap">
        <StageChip stage={step.stage} label={stageLabel} />
        <Typography variant="h6" fontWeight={600} sx={{ flexGrow: 1 }}>
          {step.step_name}
        </Typography>
        <RunStatusChip status={step.status} label={statusLabel} />
      </Box>

      <Box mb={2}>
        <Typography variant="body2" color="text.secondary">
          Step Key
        </Typography>
        <Typography
          component="div"
          sx={{
            fontFamily: "monospace",
            fontSize: "0.875rem",
            wordBreak: "break-all",
            width: "100%",
          }}
        >
          {step.step_key}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <Field label="Stage Order" value={step.stage_order} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Field
            label="Started"
            value={
              <RunTimestamp
                iso={step.started_at}
                timeZone={displayZone}
                showAbbreviation={timezoneMode === "client"}
              />
            }
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Field
            label="Finished"
            value={
              step.finished_at ? (
                <RunTimestamp
                  iso={step.finished_at}
                  timeZone={displayZone}
                  showAbbreviation={timezoneMode === "client"}
                />
              ) : (
                "—"
              )
            }
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Field
            label="Duration"
            value={formatIsoDuration(step.started_at, step.finished_at)}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {errorDetail && (
        <Accordion
          disableGutters
          elevation={0}
          sx={{ mb: 2, border: "1px solid", borderColor: "divider" }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" color="text.secondary">
              Technical details
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              component="pre"
              sx={{
                m: 0,
                fontFamily: "monospace",
                fontSize: "0.8125rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {errorDetail}
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

      <PayloadView
        value={payload}
        emptyHint={
          errorMessage ? "No additional details." : "No details reported"
        }
        boxed
        title="Details"
      />
    </Paper>
  );
}
